// apps/widget/src/screens/recommendations.screen.tsx
//
// The screen the whole product exists to reach: the clinic's own treatments,
// with session counts and prices, matched to what the scan found.
//
// Without this it is a toy scanner. The catalogue mapping is the product.

import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { send } from '../lib/postmessage';
import { useScan } from '../state';
import { TreatmentCard, type Treatment } from '../components/treatment-card';

export function RecommendationsScreen() {
  const { state, go } = useScan();
  const [treatments, setTreatments] = useState<Treatment[] | null>(null);

  useEffect(() => {
    if (!state.scanId) return;
    void api
      .recommendations(state.scanId)
      .then((r) => setTreatments(r.treatments as Treatment[]))
      .catch(() => setTreatments([]));
  }, [state.scanId]);

  const book = (treatmentId?: string): void => {
    send('gs:cta:clicked', { treatmentId, mode: 'booking' });
    go('booking');
  };

  if (!treatments) {
    return (
      <div className="gs-stack">
        {/* Same shape and height as the real cards, so nothing jumps. */}
        <div className="gs-skeleton" style={{ height: 148 }} aria-busy="true" />
        <div className="gs-skeleton" style={{ height: 148 }} aria-busy="true" />
      </div>
    );
  }

  const primary = treatments.find((x) => x.kind === 'primary');
  const supporting = treatments.filter((x) => x.kind === 'supporting');
  const clinic = state.config.tenant.name;
  const bookLabel = state.config.copy.ctaLabel || t('recommendations.book');

  // Nothing on the clinic's menu matched. A bare "no treatments matched" read
  // as a dead end; this is the moment a consultation is the right answer, so
  // the patient is invited to one. Staff see the plain fact instead.
  if (treatments.length === 0 && !state.staffView) {
    return (
      <div className="gs-stack">
        <h1 className="gs-title">{t('recommendations.nextStep')}</h1>
        <section className="gs-invite" aria-labelledby="gs-invite-title">
          <h2 id="gs-invite-title" className="gs-invite-title">
            {t('recommendations.invite.title')}
          </h2>
          <p className="gs-body">{t('recommendations.invite.body', { clinic })}</p>
          <ul className="gs-invite-points">
            {(['point1', 'point2', 'point3'] as const).map((point) => (
              <li key={point}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3.5 8.5l3 3 6-7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {t(`recommendations.invite.${point}`)}
              </li>
            ))}
          </ul>
          <button className="gs-btn gs-btn--primary" onClick={() => book()}>
            {bookLabel}
          </button>
        </section>
        <div className="gs-actions">
          <button className="gs-btn gs-btn--ghost" onClick={() => go('routine')}>
            {t('routine.title')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="gs-stack">
      <h1 className="gs-title">{t('recommendations.title')}</h1>
      <p className="gs-muted">
        {t('recommendations.subtitle', { clinic })}
      </p>

      {treatments.length === 0 && <p className="gs-body">{t('recommendations.none')}</p>}

      {/* Staff see the plan the patient was shown, without the patient's Book
          buttons — booking is not theirs to click from here. */}
      {primary && (
        <TreatmentCard
          treatment={primary}
          featured
          {...(state.staffView ? {} : { onBook: () => book(primary.id) })}
        />
      )}

      {supporting.map((treatment) => (
        <TreatmentCard
          key={treatment.id}
          treatment={treatment}
          {...(state.staffView ? {} : { onBook: () => book(treatment.id) })}
        />
      ))}

      <div className="gs-actions">
        {/* One primary CTA per screen. Here it is Book. */}
        {!state.staffView && (
          <button className="gs-btn gs-btn--primary" onClick={() => book(primary?.id)}>
            {bookLabel}
          </button>
        )}
        <button className="gs-btn gs-btn--ghost" onClick={() => go('routine')}>
          {t('routine.title')}
        </button>
      </div>
    </div>
  );
}
